import { z } from 'zod';

import { baseAttributesSchema } from './base';
import { MESSAGE_TYPE_MODULES, type MessageTypeModules } from './modules';

import { extractedQuestionsAttributesSchema } from '@/services/stores/extractedQuestionsStore';

/**
 * Assembles the message schemas from the registered per-type modules.
 *
 * This is the layer that folds the chat-bubble stores' attribute schemas into
 * the read path, so it is heavier than `modules.ts`/`behavior.ts` — consumers
 * that need only a type's discriminator or behavior should import those
 * instead of this.
 */

/**
 * Strict discriminated union of every registered type's payload.
 * Used by producers and validation — not by the tolerant read path.
 *
 * The union member type is derived from the module tuple rather than inferred
 * back off the built schema: `z.discriminatedUnion` cannot see through the
 * `map`, so the cast below feeds it the options it needs at runtime while
 * `MessageData` keeps the precise per-type union.
 */
export type MessageData = z.infer<MessageTypeModules[number]['payloadSchema']>;

const payloadSchemas = MESSAGE_TYPE_MODULES.map((module) => module.payloadSchema) as unknown as [
  z.ZodDiscriminatedUnionOption<'type'>,
  ...z.ZodDiscriminatedUnionOption<'type'>[],
];

export const messageDataSchema = z.discriminatedUnion(
  'type',
  payloadSchemas
) as unknown as z.ZodType<MessageData>;

type UnionToIntersection<Union> = (Union extends unknown ? (arg: Union) => void : never) extends (
  arg: infer Intersection
) => void
  ? Intersection
  : never;

type TypeAttributes = UnionToIntersection<z.infer<MessageTypeModules[number]['attributesSchema']>>;

/**
 * The loose read-path shape: every field optional, nothing required.
 *
 * This is deliberately not the discriminated union. It is what keeps the read
 * path tolerant — a message whose type is unknown, or whose payload fails
 * validation, drops its fields and renders as plain text instead of throwing
 * and taking the thread down with it.
 */
export type MessageAttributes = z.infer<typeof baseAttributesSchema> &
  TypeAttributes &
  z.infer<typeof extractedQuestionsAttributesSchema>;

const foldedAttributesSchema = MESSAGE_TYPE_MODULES.reduce<z.ZodObject<z.ZodRawShape>>(
  (accumulated, module) => accumulated.merge(module.attributesSchema),
  baseAttributesSchema
).merge(extractedQuestionsAttributesSchema);

/**
 * Schema for parsing Twilio message attributes.
 *
 * The backend stores message metadata at the top level with a `type`
 * discriminator; the fields present beyond that depend on the type.
 *
 * The cast restores the precise per-type shape that `reduce` erases — the fold
 * is correct at runtime but `reduce` widens each merge to a generic object,
 * and `MessageAttributes` is derived from the same modules the fold walks.
 */
export const messageAttributesSchema =
  foldedAttributesSchema.optional() as unknown as z.ZodOptional<z.ZodType<MessageAttributes>>;
